import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, Loader2, X, Clock, Trash2 } from 'lucide-react';

const CYCLING_PLACEHOLDERS = [
  'What can I do near me this weekend?',
  'I have a car and 5 hours free',
  'Remote work I can do in the evenings',
  'Ways to earn money with my spare room',
  'Side hustles near {city} without experience',
  'What can I do from home with no skills?',
  'I want passive income while I sleep',
  'Best gigs for students with flexible hours',
];

const QUICK_TABS = [
  { label: 'Near Me', query: 'earn money near me' },
  { label: 'Online', query: 'online income opportunities' },
  { label: 'Remote', query: 'remote work from home' },
  { label: 'Weekend', query: 'weekend side hustles' },
  { label: 'Passive Income', query: 'passive income ideas' },
  { label: 'Freelance', query: 'freelance work opportunities' },
  { label: 'No Experience', query: 'no experience needed earning' },
];

const STORAGE_KEY = 'hustlehub_recent_searches';
const MAX_RECENT = 5;

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveSearch(query) {
  const q = query.trim();
  if (!q) return;
  const recent = getRecentSearches().filter((s) => s !== q);
  recent.unshift(q);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export default function HeroSearchBar() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [userCity, setUserCity] = useState('London');

  // Typewriter state
  const [displayText, setDisplayText] = useState('');
  const [phIndex, setPhIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setRecentSearches(getRecentSearches());
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);

    // Detect user's city via timezone + locale heuristic
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const cityMap = {
        'America/New_York': 'New York', 'America/Chicago': 'Chicago', 'America/Denver': 'Denver',
        'America/Los_Angeles': 'Los Angeles', 'America/Toronto': 'Toronto', 'America/Vancouver': 'Vancouver',
        'America/Sao_Paulo': 'São Paulo', 'America/Mexico_City': 'Mexico City', 'America/Argentina/Buenos_Aires': 'Buenos Aires',
        'Europe/London': 'London', 'Europe/Paris': 'Paris', 'Europe/Berlin': 'Berlin',
        'Europe/Madrid': 'Madrid', 'Europe/Rome': 'Rome', 'Europe/Amsterdam': 'Amsterdam',
        'Europe/Stockholm': 'Stockholm', 'Europe/Warsaw': 'Warsaw', 'Europe/Istanbul': 'Istanbul',
        'Europe/Moscow': 'Moscow', 'Europe/Dublin': 'Dublin', 'Europe/Lisbon': 'Lisbon',
        'Europe/Brussels': 'Brussels', 'Europe/Vienna': 'Vienna', 'Europe/Zurich': 'Zurich',
        'Asia/Tokyo': 'Tokyo', 'Asia/Seoul': 'Seoul', 'Asia/Shanghai': 'Shanghai',
        'Asia/Hong_Kong': 'Hong Kong', 'Asia/Singapore': 'Singapore', 'Asia/Dubai': 'Dubai',
        'Asia/Kolkata': 'Mumbai', 'Asia/Calcutta': 'Mumbai', 'Asia/Bangkok': 'Bangkok',
        'Asia/Jakarta': 'Jakarta', 'Asia/Manila': 'Manila', 'Asia/Kuala_Lumpur': 'Kuala Lumpur',
        'Australia/Sydney': 'Sydney', 'Australia/Melbourne': 'Melbourne', 'Australia/Brisbane': 'Brisbane',
        'Pacific/Auckland': 'Auckland', 'Africa/Lagos': 'Lagos', 'Africa/Cairo': 'Cairo',
        'Africa/Johannesburg': 'Johannesburg', 'Africa/Nairobi': 'Nairobi',
      };
      // Try exact match first, then city from tz string
      if (cityMap[tz]) {
        setUserCity(cityMap[tz]);
      } else {
        const parts = tz.split('/');
        const tzCity = parts[parts.length - 1]?.replace(/_/g, ' ');
        if (tzCity && tzCity.length > 2) setUserCity(tzCity);
      }
    } catch {}

    return () => mq.removeEventListener('change', handler);
  }, []);

  // Resolve placeholders with user's city
  const resolvedPlaceholders = CYCLING_PLACEHOLDERS.map(p => p.replace('{city}', userCity));

  // Typewriter cycling
  useEffect(() => {
    if (query || isFocused || reducedMotion) return;
    const current = resolvedPlaceholders[phIndex];
    let timer;
    if (!isDeleting) {
      if (displayText.length < current.length) {
        timer = setTimeout(() => setDisplayText(current.slice(0, displayText.length + 1)), 65);
      } else {
        timer = setTimeout(() => setIsDeleting(true), 2200);
      }
    } else {
      if (displayText.length > 0) {
        timer = setTimeout(() => setDisplayText(current.slice(0, displayText.length - 1)), 30);
      } else {
        setIsDeleting(false);
        setPhIndex((phIndex + 1) % resolvedPlaceholders.length);
      }
    }
    return () => clearTimeout(timer);
  }, [displayText, isDeleting, phIndex, query, isFocused, reducedMotion, resolvedPlaceholders]);

  // Layer 3: Ripple trigger
  const triggerRipple = useCallback(() => {
    if (reducedMotion) return;
    const id = Date.now();
    setRipples(prev => [...prev, id]);
    setTimeout(() => setRipples(prev => prev.filter(r => r !== id)), 850);
  }, [reducedMotion]);

  const handleSubmit = (searchQuery) => {
    const q = (searchQuery || query).trim();
    if (!q) return;
    saveSearch(q);
    triggerRipple();
    setSubmitting(true);
    const params = new URLSearchParams({ q });
    navigate(`/ai-search?${params.toString()}`);
  };

  const removeRecent = (term) => {
    const updated = recentSearches.filter((s) => s !== term);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setRecentSearches(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      triggerRipple();
      handleSubmit();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    triggerRipple();
  };

  const showTypewriter = !query && !isFocused;

  return (
    <div className="w-full max-w-[680px] mx-auto" data-testid="hero-search-bar">
      {/* Search bar with 3-layer animation */}
      <div className="relative">
        {/* Layer 3: Ripple waves */}
        {ripples.map(id => (
          <div key={id} className="search-ripple-wave" />
        ))}

        {/* Layer 2: Breathing glow search bar */}
        <div className="search-bar-glow relative z-10">
          <div className="flex items-center bg-[#0A1628]/85 backdrop-blur-xl rounded-full pl-5 sm:pl-6 pr-1.5 sm:pr-2 shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
            {/* Input with typewriter overlay */}
            <div className="relative flex-1 min-w-0">
              {showTypewriter && (
                <div className="absolute inset-0 flex items-center pointer-events-none select-none overflow-hidden" data-testid="typewriter-text">
                  <span className="text-slate-500 text-[15px] sm:text-base whitespace-nowrap">
                    {reducedMotion ? CYCLING_PLACEHOLDERS[0] : displayText}
                    {!reducedMotion && <span className="typewriter-cursor">|</span>}
                  </span>
                </div>
              )}
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={() => setIsFocused(false)}
                placeholder={isFocused ? 'What would you like to earn from?' : ''}
                className="w-full h-14 sm:h-[60px] lg:h-16 bg-transparent text-[15px] sm:text-base text-white placeholder:text-slate-600 focus:outline-none"
                data-testid="hero-search-input"
              />
            </div>

            {/* Ask AI button with search icon */}
            <button
              onClick={() => handleSubmit()}
              disabled={!query.trim() || submitting}
              className="search-btn-sparkle h-10 sm:h-11 px-5 sm:px-6 bg-teal-500 hover:bg-teal-400 disabled:opacity-25 disabled:cursor-not-allowed text-slate-900 font-bold text-sm sm:text-[15px] rounded-full flex items-center gap-1.5 justify-center transition-all duration-200 shrink-0"
              data-testid="hero-search-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="hidden sm:inline">Ask AI</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick search tabs */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-8 sm:mt-10" data-testid="quick-search-tabs">
        {QUICK_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => { setQuery(tab.query); handleSubmit(tab.query); }}
            className="quick-tab px-3.5 py-1.5 bg-[#0B1628]/80 border border-white/[0.12] rounded-full text-[12px] text-slate-300 hover:text-teal-300"
            data-testid={`tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Recent searches — only shown if they exist */}
      {recentSearches.length > 0 && (
        <div className="flex items-center gap-1.5 mt-4 flex-wrap justify-center" data-testid="recent-searches">
          <span className="flex items-center gap-1 text-[11px] text-slate-400 mr-0.5"><Clock className="h-3 w-3" /> Recent</span>
          {recentSearches.map((s) => (
            <div
              key={s}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0B1628]/80 backdrop-blur-sm border border-white/[0.12] rounded-full text-[11px] text-slate-300 hover:bg-[#0F1D35]/90 transition-all"
              style={{ transform: 'translateZ(0)' }}
              data-testid={`recent-${s.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <button
                onClick={() => { setQuery(s); handleSubmit(s); }}
                className="hover:text-white transition-colors truncate max-w-[200px]"
              >
                {s}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); removeRecent(s); }}
                className="text-slate-500 hover:text-red-400 transition-colors shrink-0"
                aria-label={`Delete ${s}`}
                data-testid={`delete-recent-${s.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
